// src/features/tidsregistering/components/attendance/PlannedShiftBar.jsx
import React, { memo } from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import { format } from 'date-fns';
import LightTooltip from '../../../../components/common/LightTooltip';

const BAR_VERTICAL_PADDING = 6;

function PlannedShiftTooltip({ slot }) {
  const startLabel = format(slot.startDate, 'HH:mm');
  const endLabel = format(slot.endDate, 'HH:mm');
  const breakMin = slot.plannedShift?.break_minutes ?? 0;
  const totalMin = (slot.endDate - slot.startDate) / 60000;
  const workMin = totalMin - breakMin;

  return (
    <Box sx={{ p: 1.5, minWidth: 150 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Vagtplan</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>
        <Typography variant="caption" color="text.secondary">Tid</Typography>
        <Typography variant="caption" fontWeight={500}>
          {startLabel} – {endLabel}
        </Typography>
      </Box>
      {breakMin > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>
          <Typography variant="caption" color="text.secondary">Pause</Typography>
          <Typography variant="caption" fontWeight={500}>{breakMin} min</Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 3, mt: 0.25 }}>
        <Typography variant="caption" color="text.secondary">Arbejdstid</Typography>
        <Typography variant="caption" fontWeight={600}>
          {(workMin / 60).toFixed(1)} t
        </Typography>
      </Box>
    </Box>
  );
}

function PlannedShiftBar({ slot, timeToX, trackHeight }) {
  const theme = useTheme();

  if (!slot.startDate || !slot.endDate) return null;

  const leftPx = timeToX(slot.startDate);
  const rightPx = timeToX(slot.endDate);
  const widthPx = Math.max(rightPx - leftPx - 1, 2);
  const topPx = BAR_VERTICAL_PADDING;
  const heightPx = trackHeight - BAR_VERTICAL_PADDING * 2;

  const startLabel = format(slot.startDate, 'HH:mm');
  const endLabel = format(slot.endDate, 'HH:mm');

  return (
    <LightTooltip
      title={<PlannedShiftTooltip slot={slot} />}
      placement="top"
      arrow
      enterDelay={200}
    >
      <Box
        sx={{
          position: 'absolute',
          left: leftPx,
          top: topPx,
          width: widthPx,
          height: heightPx,
          backgroundColor: slot.color,
          border: `1px solid ${alpha(slot.color, 0.7)}`,
          borderRadius: '24px',
          boxShadow: theme.shadows[1],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          px: 1.5,
          overflow: 'hidden',
          zIndex: 1,
          color: 'primary.contrastText',
          cursor: 'pointer',
          transition: 'all 0.15s ease-in-out',
          '&:hover': {
            boxShadow: theme.shadows[3],
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0px)',
            boxShadow: theme.shadows[1],
          },
        }}
      >
        {widthPx > 40 && (
          <Typography
            variant="caption"
            noWrap
            sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1, letterSpacing: -0.2 }}
          >
            {startLabel}–{endLabel}
          </Typography>
        )}
      </Box>
    </LightTooltip>
  );
}

export default memo(PlannedShiftBar);
